import React, { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import axios from 'axios';

interface MetricData {
  time: string;
  cpu_usage: number;
  memory_usage: number;
  disk_usage: number;
  temperature: number;
  swap_usage: number;
}

interface SystemResourcesChartProps {
  deviceId: string;
  refreshInterval?: number; // milliseconds
  minutes?: number; // how many minutes of history to show
}

const SystemResourcesChart: React.FC<SystemResourcesChartProps> = ({
  deviceId,
  refreshInterval = 10000, // Default 10 seconds
  minutes = 10 // Default last 10 minutes
}) => {
  const [metrics, setMetrics] = useState<MetricData[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchMetrics = async () => {
    try {
      const response = await axios.get(`/api/sync/metrics/${deviceId}?minutes=${minutes}`);
      setMetrics(response.data.metrics);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, refreshInterval);
    return () => clearInterval(interval);
  }, [deviceId, refreshInterval, minutes]);

  if (loading && metrics.length === 0) {
    return <div className="text-gray-500 dark:text-gray-400">Loading metrics...</div>;
  }

  if (metrics.length === 0) {
    return <div className="text-gray-500 dark:text-gray-400">No metrics data available</div>;
  }

  // Format the data for the chart
  const chartData = metrics.map(m => ({
    time: new Date(m.time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    CPU: Number(m.cpu_usage || 0).toFixed(1),
    Memory: Number(m.memory_usage || 0).toFixed(1),
    Disk: Number(m.disk_usage || 0).toFixed(1),
    Temperature: m.temperature ? Number(m.temperature).toFixed(1) : null,
  }));

  // Chart styling that adapts to dark mode
  const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  const gridColor = isDarkMode ? '#374151' : '#e5e7eb';
  const axisColor = isDarkMode ? '#6b7280' : '#9ca3af';
  const textColor = isDarkMode ? '#9ca3af' : '#6b7280';

  // Get latest values for display
  const latestData = chartData.length > 0 ? chartData[chartData.length - 1] : null;

  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
      <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">System Resources</h3>

      {/* 2-column grid layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* CPU Chart */}
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">CPU Usage</h4>
            <span className="text-2xl font-semibold text-blue-600 dark:text-blue-400">
              {latestData?.CPU || '—'}%
            </span>
          </div>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis
                dataKey="time"
                tick={{ fontSize: 10, fill: textColor }}
                stroke={axisColor}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 10, fill: textColor }}
                stroke={axisColor}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff'
                }}
              />
              <Line
                type="monotone"
                dataKey="CPU"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
                animationDuration={300}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Memory Chart */}
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Memory Usage</h4>
            <span className="text-2xl font-semibold text-emerald-600 dark:text-emerald-400">
              {latestData?.Memory || '—'}%
            </span>
          </div>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis
                dataKey="time"
                tick={{ fontSize: 10, fill: textColor }}
                stroke={axisColor}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 10, fill: textColor }}
                stroke={axisColor}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff'
                }}
              />
              <Line
                type="monotone"
                dataKey="Memory"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
                animationDuration={300}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Disk Chart */}
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Disk Usage</h4>
            <span className="text-2xl font-semibold text-amber-600 dark:text-amber-400">
              {latestData?.Disk || '—'}%
            </span>
          </div>
          <ResponsiveContainer width="100%" height={120}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis
                dataKey="time"
                tick={{ fontSize: 10, fill: textColor }}
                stroke={axisColor}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fontSize: 10, fill: textColor }}
                stroke={axisColor}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1f2937',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#fff'
                }}
              />
              <Line
                type="monotone"
                dataKey="Disk"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
                animationDuration={300}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Temperature Chart (if available) */}
        {chartData.some(d => d.Temperature !== null) && (
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Temperature</h4>
              <span className="text-2xl font-semibold text-red-600 dark:text-red-400">
                {latestData?.Temperature || '—'}°C
              </span>
            </div>
            <ResponsiveContainer width="100%" height={120}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
                <XAxis
                  dataKey="time"
                  tick={{ fontSize: 10, fill: textColor }}
                  stroke={axisColor}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: textColor }}
                  stroke={axisColor}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1f2937',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#fff'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="Temperature"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={false}
                  animationDuration={300}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemResourcesChart;
